import api from "../utils/api";

/**
 * Filter that selects the targeted courses for a global-change operation.
 *
 * - `allCourses: true` → the operation applies to every course in the DB.
 * - `courseIds: string[]` → the operation applies only to the listed course IDs.
 *
 * Exactly one of the two must be provided. The backend rejects any other
 * combination with a 400.
 */
export interface GlobalChangesCourseFilter {
  allCourses?: boolean;
  courseIds?: string[];
}

/**
 * A bucket from the device-limit preview. The backend groups users by their
 * current `deviceLimit` value and reports the count per bucket so admins can
 * see the spread before applying a change.
 */
export interface DeviceLimitBucket {
  value: number;
  count: number;
}

/**
 * Result of `previewGlobalChange`. Fields that don't apply to the chosen
 * operation are `null`.
 */
export interface GlobalChangesPreviewResult {
  status: "success";
  operation: "extend" | "deviceLimit";
  enrollmentsAffected: number | null;
  studentsAffected: number | null;
  deviceLimitBuckets: DeviceLimitBucket[] | null;
}

/**
 * Duration preset keys accepted by the extend-enrollments endpoint.
 * Mirrors the `presetMap` in the backend controller.
 */
export type ExtendPreset = "3m" | "6m" | "9m" | "1y" | "2y" | "3y";

/**
 * Per-enrollment entry returned in the `sample` array of an extend operation.
 * `previousExpiresAt` is `null` if the enrollment had no expiry before.
 */
export interface ExtendedEnrollmentSample {
  id: string;
  courseId: string;
  courseTitle: string;
  previousExpiresAt: string | null;
  newExpiresAt: string;
}

export interface ExtendEnrollmentsResult {
  status: "success";
  message: string;
  count: number;
  sample: ExtendedEnrollmentSample[];
}

export interface UpdateDeviceLimitResult {
  status: "success";
  message: string;
  matched: number;
  modified: number;
}

/**
 * Wrapper around the three new admin bulk-operation endpoints introduced by
 * the Global Changes feature. Each method mirrors one route under
 * `/api/global-changes`.
 *
 * Note: the shared `api` client already unwraps axios responses to the payload
 * body (see `utils/api.js`), so each method returns the unwrapped result
 * directly — callers do not need to read `.data` again.
 */
export const globalChangesService = {
  /**
   * Returns the number of records the requested operation would touch,
   * without mutating anything. The admin uses this to populate the
   * "X enrollments affected" preview line before confirmation.
   */
  preview: async (
    params: GlobalChangesCourseFilter & {
      operation: "extend" | "deviceLimit";
    },
  ): Promise<GlobalChangesPreviewResult> => {
    const response = await api.post("/api/global-changes/preview", params);
    return response;
  },

  /**
   * Extend the `enrollmentExpiresAt` of every enrollment matching the
   * course filter by either a preset duration or a custom date.
   *
   * - Provide exactly one of `preset` or `customDate`.
   * - The new expiry for each enrollment is `max(today, currentExpiry) + duration`.
   * - The response includes up to 20 sample rows with course titles so the
   *   admin can spot-check what changed.
   */
  extendEnrollments: async (params: {
    preset?: ExtendPreset;
    customDate?: string;
    allCourses?: boolean;
    courseIds?: string[];
  }): Promise<ExtendEnrollmentsResult> => {
    const response = await api.post(
      "/api/global-changes/extend-enrollments",
      params,
    );
    return response;
  },

  /**
   * Update `User.deviceLimit` for every distinct student with an enrollment
   * in the matching courses. Idempotent: re-running with the same value
   * returns `modified: 0`.
   */
  updateDeviceLimit: async (params: {
    deviceLimit: number;
    allCourses?: boolean;
    courseIds?: string[];
  }): Promise<UpdateDeviceLimitResult> => {
    const response = await api.post(
      "/api/global-changes/update-device-limit",
      params,
    );
    return response;
  },
};

export default globalChangesService;